import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Wand2, Scissors, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { SuggestionField } from "./AISuggestionsDiff";

export type QuickAction = "improve_clarity" | "tighten" | "better_distractor";

interface QuickAIActionsProps {
  field: SuggestionField;
  currentValue: string;
  mcqId?: string;
  mcqContext: {
    stem: string;
    option_a: string;
    option_b: string;
    option_c: string;
    option_d?: string | null;
    option_e?: string | null;
    correct_option: string;
    explanation?: string | null;
    topic_name?: string | null;
  };
  onSuggestion: (s: { field: SuggestionField; suggested: string; rationale: string }) => void;
}

const ACTIONS: {
  key: QuickAction;
  label: string;
  icon: typeof Wand2;
  appliesTo: (field: SuggestionField) => boolean;
}[] = [
  {
    key: "improve_clarity",
    label: "Improve clarity",
    icon: Wand2,
    appliesTo: () => true,
  },
  {
    key: "tighten",
    label: "Tighten",
    icon: Scissors,
    appliesTo: () => true,
  },
  {
    key: "better_distractor",
    label: "Stronger distractor",
    icon: ShieldAlert,
    appliesTo: (f) => f.startsWith("option_"),
  },
];

export function QuickAIActions({
  field,
  currentValue,
  mcqId,
  mcqContext,
  onSuggestion,
}: QuickAIActionsProps) {
  const { toast } = useToast();
  const [busy, setBusy] = useState<QuickAction | null>(null);

  const run = async (action: QuickAction) => {
    if (!currentValue?.trim()) {
      toast({
        title: "Nothing to improve",
        description: "Add some content before asking AI to refine it.",
        variant: "destructive",
      });
      return;
    }
    setBusy(action);
    try {
      const { data, error } = await supabase.functions.invoke("ai-mcq-quick-fix", {
        body: { field, action, currentValue, mcqContext, mcqId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      onSuggestion({
        field,
        suggested: data.suggestion,
        rationale: data.rationale,
      });
      toast({
        title: "AI suggestion ready",
        description: "Review and apply or reject below.",
      });
    } catch (e: any) {
      toast({
        title: "AI quick fix failed",
        description: e?.message || "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  };

  const applicable = ACTIONS.filter((a) => a.appliesTo(field));

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" aria-hidden />
      {applicable.map((a) => {
        const Icon = a.icon;
        return (
          <Button
            key={a.key}
            type="button"
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={() => run(a.key)}
            className="h-7 px-2 text-xs"
          >
            {busy === a.key ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <Icon className="h-3 w-3 mr-1" />
            )}
            {a.label}
          </Button>
        );
      })}
    </div>
  );
}
